import type { View, ViewProps } from "@-/com.npmjs.react-native";
import Animated, {
  type DerivedValue,
  useAnimatedStyle,
} from "@-/com.npmjs.react-native-reanimated";
import type { Ref } from "react";

/**
 * Hides the bottom `clip` points of its content without moving the content or changing its layout:
 * a frame moved up by `clip` with its overflow hidden, around the content moved back down by as
 * much. The outer view keeps the caller's placement (`className`, `style`) and never moves. It
 * clips only while `isClipping`, so at rest nothing drawn past its box (a shadow, a menu) is cut.
 */
export function LearnMapBottomClip({
  children,
  clip,
  contentClassName = "",
  isClipping,
  pointerEvents = "box-none",
  ref,
  ...props
}: ViewProps & {
  /** How many points to hide at the bottom. */
  clip: DerivedValue<number>;
  /** Placement of the content within the clip, e.g. its flex direction. */
  contentClassName?: string;
  isClipping: boolean;
  ref?: Ref<View>;
}) {
  const frameStyle = useAnimatedStyle(() => ({ transform: [{ translateY: -clip.get() }] }));
  const contentStyle = useAnimatedStyle(() => ({ transform: [{ translateY: clip.get() }] }));
  return (
    // Animated, so that an animated ref on it can be measured (`LearnMapSheetCovered`).
    <Animated.View {...props} collapsable={false} pointerEvents={pointerEvents} ref={ref}>
      <Animated.View
        className={isClipping ? "size-full overflow-hidden" : "size-full"}
        pointerEvents="box-none"
        style={frameStyle}
      >
        <Animated.View
          className={`size-full ${contentClassName}`}
          pointerEvents="box-none"
          style={contentStyle}
        >
          {children}
        </Animated.View>
      </Animated.View>
    </Animated.View>
  );
}
